import {
  Box,
  Column,
  TablePaginated,
  TablePaginatedOptions,
  Typography,
} from '@acceso/design-system';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { StateActiveInTable } from '@/components/StateActiveInTable/StateActiveInTable';
import { TableRow } from '@/components/TableRow';
import { TableRowSchedule } from '@/components/TableRowSchedule/TableRowSchedule';
import { TableUpdatedBy } from '@/components/TableUpdatedBy';
import { useChannels } from '@/features/broadcasters/api/get-channels';
import { CHANNEL_MEDIA_TYPE_TO_ICON } from '@/features/broadcasters/constants/icons';
import { useChannelMediaTypeOptions } from '@/features/broadcasters/hooks/useChannelMediaTypeOptions';
import { useChannelOptions } from '@/features/broadcasters/hooks/useChannelOptions';
import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import { useProductionCenterOptions } from '@/features/production-center/hooks/useProductionCenterOptions';
import { useUserOptions } from '@/features/users/hooks/useUserOptions';
import { useStateOptions } from '@/hooks/useStateOptions';
import { State } from '@/types';
import { withAppTzDateFilter } from '@/utils/app-tz-date-range';
import { createTableFilterOptions } from '@/utils/table-filters';

import { useChannelRecordingFilters } from '../../stores/channel-recording-filters';
import { ChannelRecordingType } from '../../types';

type ChannelRecordingsTableProps = {
  items: ChannelRecordingType[];
  totalItems: number;
  activeItem: ChannelRecordingType | null;
  onDelete?: (item: ChannelRecordingType) => void;
  onDuplicate?: (item: ChannelRecordingType) => void;
  onUpdate?: (item: ChannelRecordingType) => void;
  onSelect?: (item: ChannelRecordingType) => void;
  isFetching?: boolean;
};

export function ChannelRecordingsTable({
  items,
  totalItems,
  activeItem,
  onUpdate,
  onDuplicate,
  onSelect,
  onDelete,
  isFetching,
}: ChannelRecordingsTableProps) {
  const { t } = useTranslation();
  const { data: { data: channels = [] } = {} } = useChannels({
    params: { isParentChannel: false },
  });
  const channelOptions = useChannelOptions({
    isParentChannel: false,
  });
  const userOptions = useUserOptions();
  const channelMediaTypeOptions = useChannelMediaTypeOptions();
  const productionCenterOptions = useProductionCenterOptions();
  const { setFilters, paginationValues, setPage, setRows, setSortBy, setSearchQuery } =
    useChannelRecordingFilters();
  const stateOptions = useStateOptions();

  const options: Partial<TablePaginatedOptions> = {
    pagination: {
      changePage: setPage,
      changeRows: setRows,
      currentPage: paginationValues.currentPage,
      itemsPerPage: paginationValues.rowsPerPage,
    },
    setSortBy,
    sortBy: paginationValues.sortBy,
    onSearchChange: setSearchQuery,
    dateFilter: withAppTzDateFilter({
      name: 'created_at',
      placeholder: t('Fecha de creación'),
      onChange: value => {
        setFilters({
          gte: value.start?.toISOString(),
          lte: value.end?.toISOString(),
        });
      },
    }),
    filters: [
      {
        name: 'media_type',
        placeholder: t('Soporte'),
        options: createTableFilterOptions({
          options: channelMediaTypeOptions,
          allOptionLabel: t('Cualquier soporte'),
        }),
        onChange: value => {
          setFilters({
            channelMediaType: value as unknown as ChannelMediaTypeEnum,
          });
        },
      },
      {
        name: 'production_center',
        placeholder: t('Centro de producción'),
        options: createTableFilterOptions({
          options: productionCenterOptions,
          allOptionLabel: t('Cualquier centro de producción'),
        }),
        onChange: value => {
          setFilters({
            productionCenterIdIn: [value as unknown as string],
          });
        },
      },
      {
        name: 'channel_id',
        placeholder: t('Emisora'),
        options: createTableFilterOptions({
          options: channelOptions,
          allOptionLabel: t('Cualquier emisora'),
        }),
        onChange: value => {
          setFilters({
            channelId: value as unknown as string,
          });
        },
      },
      {
        name: 'state',
        placeholder: t('Estado'),
        options: createTableFilterOptions({
          options: stateOptions,
          allOptionLabel: t('Cualquier estado'),
        }),
        onChange: value => {
          setFilters({
            state: value as unknown as State,
          });
        },
      },
      {
        name: 'updated_by',
        placeholder: t('Actualizado por'),
        options: createTableFilterOptions({
          options: userOptions,
          allOptionLabel: t('Actualizado por cualquiera'),
        }),
        onChange: value => {
          setFilters({
            updatedByIn: [value as unknown as string],
          });
        },
      },
    ],
  };
  const columns: Column<ChannelRecordingType>[] = [
    {
      id: 'description',
      title: t('Descripción'),
      sortable: true,
      valueFn: item => item.description,
      onColumnClick: item => onSelect?.(item),
      render: item => <TableRow showTooltip title={item.description} />,
    },
    {
      id: 'broadcaster',
      title: t('Emisora'),
      onColumnClick: item => onSelect?.(item),
      sortable: true,
      valueFn: item => item.channel_id,
      render: item => {
        const channel = channels.find(channel => channel.id === item.channel_id);

        if (!channel) {
          return null;
        }

        const Icon = CHANNEL_MEDIA_TYPE_TO_ICON[channel.media_type];

        return (
          <Box className="flex gap-2">
            <Icon className="mt-1" />

            <div className="flex flex-col gap-1">
              <Typography className="text-xs font-semibold text-text-dark-10">
                {channel.name}
              </Typography>

              <Typography className="text-xs text-text-medium-10">{channel.short_name}</Typography>
            </div>
          </Box>
        );
      },
    },
    {
      id: 'state',
      title: t('Estado'),
      sortable: true,
      valueFn: item => item.schedule.toString(),
      onColumnClick: item => onSelect?.(item),
      render: item => <StateActiveInTable status={item.state} />,
    },
    {
      id: 'date',
      title: t('Programación'),
      sortable: true,
      valueFn: item => `${item.start_datetime} - ${item.end_datetime}`,
      onColumnClick: item => onSelect?.(item),
      render: item => (
        <TableRowSchedule
          startDatetime={item.start_datetime}
          endDatetime={item.end_datetime}
          schedule={item.schedule}
        />
      ),
    },
    {
      id: 'updated_by',
      title: t('Actualizado por'),
      sortable: true,
      valueFn: item => item.updated_by,
      onColumnClick: item => onSelect?.(item),
      render: item => {
        return <TableUpdatedBy item={item} />;
      },
    },
    {
      id: 'actions',
      title: '',
      className: 'items-end',
      width: '5%',
      render: item => (
        <RenderActionButtons
          item={item}
          onUpdate={() => onUpdate?.(item)}
          onDuplicate={() => onDuplicate?.(item)}
          onDelete={() => onDelete?.(item)}
        />
      ),
    },
  ];

  return (
    <TablePaginated
      options={options}
      columns={columns}
      data={items}
      activeItem={activeItem}
      totalItems={totalItems}
      isFetching={isFetching}
      ignoreEmpty
    />
  );
}
